import { Skeleton } from "@somethings-ui/web";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo } from "react";
import { InfiniteScroll } from "@/components/shared/infinite-scroll";
import { useActionPanelScrollRef } from "../action-panel-scroll";
import { HistoryEntryRow } from "./history-entry-row";
import {
  groupEntriesByDay,
  HISTORY_DAY_TIMEZONE_FALLBACK,
  type HistoryEntry,
} from "./history-model";
import { HistoryRetryPrompt } from "./history-retry";

const HEADER_ESTIMATE_PX = 40;
const ENTRY_ESTIMATE_PX = 60;
const HISTORY_ROW_OVERSCAN = 8;

type HistoryVirtualItem =
  | { kind: "header"; dayKey: string; label: string }
  | { kind: "entry"; entry: HistoryEntry };

interface HistoryListViewProps {
  entries: HistoryEntry[];
  timezone?: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onSelectEntry: (entry: HistoryEntry) => void;
}

export function HistoryListView({
  entries,
  timezone = HISTORY_DAY_TIMEZONE_FALLBACK,
  isLoading,
  isError,
  refetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onSelectEntry,
}: HistoryListViewProps) {
  const scrollRef = useActionPanelScrollRef();
  const groups = useMemo(
    () => groupEntriesByDay(entries, undefined, timezone),
    [entries, timezone],
  );
  const flatItems = useMemo((): HistoryVirtualItem[] => {
    const items: HistoryVirtualItem[] = [];
    for (const group of groups) {
      items.push({
        kind: "header",
        dayKey: group.dayKey,
        label: group.label,
      });
      for (const entry of group.entries) {
        items.push({ kind: "entry", entry });
      }
    }
    return items;
  }, [groups]);

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      flatItems[index]?.kind === "header"
        ? HEADER_ESTIMATE_PX
        : ENTRY_ESTIMATE_PX,
    overscan: HISTORY_ROW_OVERSCAN,
    getItemKey: (index) => {
      const item = flatItems[index];
      if (!item) return index;
      return item.kind === "header" ? `h:${item.dayKey}` : `e:${item.entry.id}`;
    },
  });

  if (isLoading) return <HistoryRowsSkeleton />;
  if (isError) return <HistoryError onRetry={refetch} />;
  if (groups.length === 0) return <HistoryEmpty />;

  return (
    <div className="flex flex-col pb-4">
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = flatItems[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {item.kind === "header" ? (
                <div className="px-4 pb-2 pt-4">
                  <span className="font-body text-[10px] uppercase tracking-wide text-text-tertiary">
                    {item.label}
                  </span>
                </div>
              ) : (
                <div className="px-2">
                  <HistoryEntryRow
                    entry={item.entry}
                    onClick={onSelectEntry}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <InfiniteScroll
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoading={isFetchingNextPage}
        root={scrollRef}
        showLoader
      />
    </div>
  );
}

function HistoryEmpty() {
  return (
    <p className="px-4 py-6 font-body text-body-3 text-text-tertiary">
      No history yet - activities and content will show up here.
    </p>
  );
}

function HistoryError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-1 px-4 py-6">
      <HistoryRetryPrompt message="Couldn't load history." onRetry={onRetry} />
    </div>
  );
}

function HistoryRowsSkeleton() {
  return (
    <div className="flex flex-col px-2 pt-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex w-full items-center gap-3 p-2">
          <Skeleton className="size-9 shrink-0 rounded-[10px]" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
